import { defineTable } from "convex/server";
import { v } from "convex/values";
import { Validator } from "convex/values";

/**
 *
 *
 * Validadores compartidos para toda la aplicación
 */
export const sharedValidators = {
  currency: v.union(
    v.object({
      value: v.literal("HNL"),
      label: v.literal("Lempira"),
    }),
    v.object({
      value: v.literal("USD"),
      label: v.literal("Dólar estadounidense"),
    }),
    v.object({
      value: v.literal("EUR"),
      label: v.literal("Euro"),
    }),
    v.object({
      value: v.literal("GBP"),
      label: v.literal("Libra esterlina"),
    }),
  ),
  currencyValue: v.union(
    v.literal("HNL"),
    v.literal("USD"),
    v.literal("EUR"),
    v.literal("GBP"),
  ),
};

/**
 * Estado de sort visual persistido en la BD. La UI lo usa mientras edita y
 * el PDF lo respeta al generar el documento. Cada elemento representa una
 * columna activa del multi-sort; `priority` es 0-based (orden de aplicación).
 * El cliente sólo persiste `column` + `direction`; `priority` se recalcula
 * al leer (es derivable del índice en el array).
 *
 * Compartido entre quotations, invoices y cualquier documento que persista
 * un sort visual de la tabla de ítems.
 */
export const validatorDisplaySortItem = v.object({
  column: v.string(),
  direction: v.union(v.literal("asc"), v.literal("desc")),
  priority: v.number(),
});

/**
 * Sello de inmutabilidad (ver `lib/records/lock.ts`). Un registro sellado solo
 * admite los cambios que declara la politica de su tabla; el guard corre como
 * trigger sobre `ctx.db`, asi que aplica a toda escritura (segura o directa).
 */
export const recordLock = v.object({
  level: v.union(v.literal("sealed"), v.literal("frozen")),
  at: v.number(),
  by: v.optional(v.id("users")),
  reason: v.string(),
});

export const internalControl = v.object({
  companyId: v.id("companies"),
  branchId: v.optional(v.id("branches")),
  state: v.union(
    v.literal("active"),
    v.literal("inactive"),
    v.literal("pending"),
    v.literal("archived"),
    v.literal("deleted"),
  ),
  deletedAt: v.optional(v.number()),
  deletedBy: v.optional(v.id("users")),
  restoredAt: v.optional(v.number()),
  restoredBy: v.optional(v.id("users")),
  createdAt: v.optional(v.number()),
  updatedAt: v.optional(v.number()),
  createdBy: v.optional(v.id("users")),
  updatedBy: v.optional(v.id("users")),
  canView: v.array(v.id("users")),
  canEdit: v.array(v.id("users")),
  canDelete: v.array(v.id("users")),
  canShare: v.array(v.id("users")),
  sharingControl: v.union(
    v.literal("public"),
    v.literal("private"),
    v.literal("protected"),
    v.literal("restricted"),
    v.literal("custom"),
  ),
  countsTowardsLimit: v.optional(v.boolean()),
  lock: v.optional(recordLock),
});

/**
 * Aplica automáticamente los índices estándar de internalControl a una tabla.
 * Los índices se pueden seguir encadenando después de llamar esta función.
 *
 * @param validator - El validador de la tabla (v.object({...}))
 * @returns defineTable con los índices de internalControl ya aplicados
 *
 * @example
 * ```ts
 * export const quotations = defineTableWithInternalControl(validatorQuotation)
 *   .index("by_client_id", ["clientId"])
 *   .index("by_status", ["status"])
 *   .index("by_code", ["code"]);
 * ```
 */
export function defineTableWithInternalControl<
  T extends Validator<any, any, any>,
>(validator: T) {
  return defineTable(validator)
    .index("by_company_id", ["internalControl.companyId"])
    .index("by_branch_id", ["internalControl.branchId"])
    .index("by_state", ["internalControl.state"])
    .index("by_company_and_state", [
      "internalControl.companyId",
      "internalControl.state",
    ])
    .index("by_company_and_branch", [
      "internalControl.companyId",
      "internalControl.branchId",
    ])
    .index("by_branch_and_state", [
      "internalControl.branchId",
      "internalControl.state",
    ])
    .index("by_created_by", ["internalControl.createdBy"])
    .index("by_updated_by", ["internalControl.updatedBy"])
    .index("by_created_at", ["internalControl.createdAt"])
    .index("by_company_and_created_at", [
      "internalControl.companyId",
      "internalControl.createdAt",
    ]);
}

/**
 * Create a validator object from explicit field definitions.
 * This is the recommended approach for defining reusable validators.
 *
 * @example
 * ```ts
 * // Define once
 * export const saleInputValidators = validatorObject({
 *   clientId: v.id("clients"),
 *   sellerId: v.optional(v.id("sellers")),
 *   title: v.optional(v.string()),
 *   description: v.optional(v.string()),
 *   status: salesStatus,
 *   version: v.optional(v.number()),
 *   parentQuotationId: v.optional(v.id("quotations")),
 *   subtotal: v.number(),
 *   taxAmount: v.number(),
 *   totalAmount: v.number(),
 *   currency: v.optional(v.string()),
 *   paymentTerms: v.optional(v.string()),
 *   notes: v.optional(v.string()),
 * });
 *
 * // Reuse in multiple places
 * args: saleInputValidators
 * ```
 */
export function validatorObject<
  T extends Record<string, Validator<any, any, any>>,
>(validators: T): T {
  return validators;
}

/**
 * Omit specific fields from a validator object
 *
 * @example
 * ```ts
 * const updateValidators = omitValidators(saleInputValidators, ["parentQuotationId"]);
 * ```
 */
export function omitValidators<
  T extends Record<string, Validator<any, any, any>>,
  K extends keyof T,
>(validators: T, keys: K[]): Omit<T, K> {
  const result = { ...validators };
  for (const key of keys) {
    delete result[key];
  }
  return result;
}

/**
 * Pick specific fields from a validator object
 *
 * @example
 * ```ts
 * const minimalValidators = pickValidators(saleInputValidators, ["clientId", "status"]);
 * ```
 */
export function pickValidators<
  T extends Record<string, Validator<any, any, any>>,
  K extends keyof T,
>(validators: T, keys: K[]): Pick<T, K> {
  const result: any = {};
  for (const key of keys) {
    result[key] = validators[key];
  }
  return result;
}

/**
 * Merge multiple validator objects
 *
 * @example
 * ```ts
 * const createSaleValidators = mergeValidators(
 *   saleInputValidators,
 *   { additionalField: v.string() }
 * );
 * ```
 */
export function mergeValidators<
  T extends Record<string, Validator<any, any, any>>,
  U extends Record<string, Validator<any, any, any>>,
>(validators1: T, validators2: U): T & U {
  return { ...validators1, ...validators2 };
}

/**
 * Make all fields in a validator object optional
 *
 * @example
 * ```ts
 * const optionalSaleValidators = makeOptional(saleInputValidators);
 * ```
 */
export function makeOptional<
  T extends Record<string, Validator<any, any, any>>,
>(validators: T): Record<keyof T, Validator<any, "optional", any>> {
  const result: any = {};
  for (const key in validators) {
    const validator = validators[key];
    // Wrap in optional if not already optional
    result[key] = v.optional(validator as any);
  }
  return result;
}

/**
 * Make specific fields in a validator object optional
 *
 * @example
 * ```ts
 * const partialValidators = makeFieldsOptional(saleInputValidators, ["title", "description"]);
 * ```
 */
export function makeFieldsOptional<
  T extends Record<string, Validator<any, any, any>>,
  K extends keyof T,
>(validators: T, keys: K[]): T & { [P in K]: Validator<any, "optional", any> } {
  const result: any = { ...validators };
  for (const key of keys) {
    result[key] = v.optional(validators[key] as any);
  }
  return result;
}
